import forge from 'node-forge'
import { captureHandledError } from 'services/sentryLog'
import { VAULT_WRITE_FORMAT_MAX_KNOWN } from 'vaultWriteFormat'
import { getRsKeyObjFromResource } from './resourceKey'
import { isVaultV2 } from './format'
import { rewriteV1ToV2 } from './forwardSweep'
import { collectKeyTargets, verifySavedKeyTargets } from './keyTargets'

/**
 * **금고 정방향 스윕 순회 엔진** (PR 7-a).
 *
 * 설계: `backend/docs/design/vault_forward_sweep/`
 *
 * 언락 직후 배경에서 돌며, 사용자가 편집 가능한 금고 값을 v1 → v2 로 옮긴다.
 * 값 하나의 변환은 `forwardSweep.js` 가, 비교·교체는 서버가 한다
 * (`POST /v1/resources/vault-format/rewrite` — CAS).
 */

/** 🛑 `'my'` 로 바꾸지 마라 (설계 D9). `scope='my'` 는 `Resource.owner_id` 만 필터하고
 *  ResourceKey 조인이 없어(`backend/services/bulk_resource_service.py:132-141`)
 *  **키 없는 레코드를 반환한다** — 그것이 F9 의 입구다. `'all'` 은
 *  `_get_accessible_resource_ids_subquery` 로 키를 보장한다. */
const SCOPE = 'all'

/** 한 페이지 크기. `limit=0`(전량 한 응답)은 상한이 없어 쓰지 않는다 (spec §7.2 = F6). */
const PAGE_SIZE = 200

/** 서버 요청 모델의 상한 (`backend/api/model/vault_format_rewrite.py:24`). */
const MAX_TARGETS = 100

/**
 * **실패가 아닌 실패 사유** (설계 D9).
 * - `PERMISSION_DENIED` — 읽기 전용 공유다. 그 리소스는 소유자·편집자가 변환한다
 * - `NOT_FOUND` — 그 사이 삭제됐다
 *
 * 이 둘을 실패로 세면 읽기 전용 공유가 하나라도 있는 사용자는 완료 판정을 영원히 못 만족한다.
 */
const BENIGN_REASONS = ['PERMISSION_DENIED', 'NOT_FOUND']

/**
 * **동시성 레이스** — 스윕이 읽은 뒤 사용자가 그 레코드를 고쳤다는 뜻이다(CAS 불일치).
 *
 * 🛑 **한 건은 정상이고 다음 언락에 멱등하게 재시도된다.** 그래서 `ERROR` 와 같은 등급으로
 * 보고하면 Sentry 가 정상 동작으로 오염된다(PR #1999 리뷰 minor). 그렇다고 조용히 버릴 수도
 * 없다 — **서버 로그는 사유를 싣지 않으므로**(`backend/routers/v1/resource.py:1197-1207` 이
 * `failed_row_ids` 만 남긴다) 사유별 분포를 볼 수 있는 곳이 여기뿐이다.
 * 그래서 **버리지 않고 태그만 분리**해 운영자가 따로 필터·음소거할 수 있게 한다.
 *
 * 의미가 있는 것은 한 건이 아니라 **비율**이다 — 한 세션 안에서는 알 수 없으므로 클라이언트가
 * 이것으로 중단하지 않는다(설계 §9.3 — 중단 판정은 운영자 몫).
 */
const RACE_REASONS = ['CONFLICT']

/**
 * 🛑 **모듈 스코프 재진입 가드.** `UMK_LOGIN` 은 `takeEvery`(`redux/user/sagas.js:836`)라
 * 직렬화되지 않고, 스윕은 fire-and-forget 이라 **사가보다 오래 산다** — 사가가 끝나
 * `umkProcessing` 이 false 가 된 뒤에도 계속 돈다. 그래서 `LoginMasterPW/index.js:229` 의
 * 기존 가드가 스윕에는 닿지 않는다(자동잠금 → 재입력 경로).
 */
let inFlight = false

/**
 * 이 레코드 수마다 이벤트 루프에 양보한다 (G3).
 *
 * 🛑 **양보가 필요한 이유는 forge 가 동기이기 때문이다.** 키 파생(`pUtils.js:1369`
 * RSA 개인키 복호, 실측 12ms/회)이 순수 JS 동기 연산이라, fire-and-forget 으로 띄워도
 * 메인 스레드를 잡고 있는 동안 화면이 그려지지 못한다(spec §7.3 — dev 실측 약 5초).
 * 총 작업량은 줄지 않고 **첫 렌더가 끼어들 틈**만 만든다.
 */
const YIELD_EVERY_RECORDS = 10

/** 이벤트 루프에 한 번 양보한다. `setTimeout(0)` 은 매크로태스크라 렌더가 끼어들 수 있다. */
const yieldToEventLoop = () =>
  new Promise((resolve) => {
    setTimeout(resolve, 0)
  })

/**
 * **방향 서술자** (PR 7-b, 설계 spec §2.2 K6).
 *
 * 🛑 **방향 의존 지점이 3곳이다** — 게이트·필터·변환기. 하나만 바꾸면 역방향이
 * **조용한 무동작**이 된다(spec G5 = F12 유형): 게이트가 닫혀 시작조차 못 하거나,
 * 시작해도 필터가 전 레코드를 건너뛴다. 그래서 셋을 한 객체로 묶는다.
 *
 * 🛑 **`keyCtx` 는 어댑터다** — `{ decrypt(b64), encrypt(plain) }`. `rsKeyObj` 가 이미 그
 * 계약을 만족하므로(`pUtils.js:1378`·`:1395`) 리소스 경로는 무수정이고, 열쇠 2종만 같은
 * 모양의 래퍼를 호출부가 만든다.
 *
 * @typedef {Object} Direction
 * @property {(blob: string, keyCtx: object) => Promise<string|null>} convert
 * @property {(target: {content: string}) => boolean} hasWork
 * @property {(writeFormat: number|undefined) => boolean} gate
 */
const FORWARD = {
  convert: rewriteV1ToV2,
  hasWork: (t) => !isVaultV2(forge.util.decode64(t.content)),
  gate: (writeFormat) => writeFormat === VAULT_WRITE_FORMAT_MAX_KNOWN,
}

/** 테스트 전용 — 모듈 스코프 가드를 되돌린다. 모듈 스코프 상태라 테스트 간 누수를 막을 방법이 없다. */
export const resetSweepGuardForTest = () => {
  inFlight = false
}

/**
 * 항목이 어느 표에 저장돼 있는지.
 *
 * 🛑 **서버 라우팅 규칙의 복제다** (`backend/api/resource.py:1138-1142`) —
 * `type==='note'` 는 `encrypt` 와 무관하게 `resource_note` 로, 그 외 암호화 항목은
 * `resource_secret` 으로 간다. PR 7-b 가 서버 응답에 `table` 을 실어주면 이 함수를 지운다(D8).
 */
const tableOf = (item) => (item.type === 'note' ? 'resource_note' : 'resource_secret')

/**
 * 암호화된 값이고 첨부가 아닌 것만 대상이다. 첨부 본문(S3)은 이번 범위 밖(spec §4).
 *
 * 🛑 **빈 문자열도 뺀다** (`!= null` 이 아니라 truthy 검사). 실측: 실제 v1 복호기에 빈 값을
 * 주면 `Invalid IV length; got 0 bytes` 로 **던진다**(`pUtils.js:81-101` 재현). D10 이 복호
 * 실패를 "전체 중단"으로 묶으므로 그런 행 하나가 **그 사용자의 스윕을 매 언락마다 같은
 * 지점에서 영구히 막는다.** 빈 값은 무결성 문제가 아니라 데이터 위생 문제다.
 * 알려진 클라이언트는 빈 값을 쓰지 않지만(`helper.js:2547` 이 `null` 을 쓴다) API 는 받아준다
 * (`backend/api/model/resource.py:47-48` 이 encrypt=true 면 검증을 건너뛴다).
 */
const isSweepTarget = (item) =>
  item != null && item.encrypt === true && item.type !== 'files' && !!item.content

/**
 * 편집 권한이 있는 리소스인가 — **items(`resource_secret`/`resource_note`) 전용 게이트**다.
 *
 * 🛑 **`group_key` 에는 쓰지 않는다** (PR #2022 리뷰 major). 서버는 테이블마다 판정 주체가
 * 다르다 (`vault_format_rewrite_service.py:135-140`):
 *
 *     resource_secret·resource_note → resource_perms[...] in EDIT_PERMISSIONS   ← 편집 권한
 *     user_key                      → row.user_id == user.id
 *     group_key                     → row.group_id in group_ids                 ← 구성원 여부만
 *
 * 설계 D9 는 *"편집 권한에 없으면 서버가 `PERMISSION_DENIED` 로 거부하니 요청 자체를 안
 * 보낸다"* 를 근거로 이 게이트를 `group_key` 에 걸었는데, **그 전제가 `group_key` 에는
 * 성립하지 않는다.** 읽기 전용으로만 공유된 그룹은 어느 구성원이 언락해도 걸러져
 * **`group_key` 가 v1 에 영구히 남는다** — 오류도 안 나는 조용한 무동작이다.
 * 구성원이면 그룹 키 재료를 이미 쥐고 있으므로 재암호화할 자격이 있다(권한 상승 아님).
 *
 * 🛑 **`record.permission` 이다 — `record.resource_key.permission` 이 아니다.**
 * 초판이 후자를 읽었고 bulk 응답에 그런 필드가 **없었다.** 최상위가 이 저장소의 관례다:
 * 단건 응답 `api/resource.py:810`·`:1099`, 프론트 `RecordEdit.js:538` `individual.permission`.
 *
 * 🛑 `permission` 이 없으면 **대상이 아니다.** 그 필드를 안 주는 서버에서 통과시키면 매
 * 언락마다 `PERMISSION_DENIED` 를 받는다 — 배포 전 실측에서 관측된 `4× PERMISSION_DENIED`
 * 가 그것이다. 상수는 서버의 `EDIT_PERMISSIONS`(`vault_format_rewrite_service.py:26`)와 같다.
 */
const EDIT_PERMISSIONS = [7, 15]

/**
 * 🛑 **"편집 권한인가" 가 아니라 "읽기 전용인 것이 확실한가" 다** — 판정을 fail-open 으로 둔다.
 *
 * `permission` 은 이 저장소에서 **프론트보다 늦게 배포될 수 있다**(백엔드·프론트 파이프라인이
 * 분리돼 있다). fail-closed 로 두면 그 사이 필드가 `undefined` 라 **모든 레코드가 읽기 전용으로
 * 판정되어 items 스윕이 통째로 멈춘다** — 이 PR 이 `group_key` 에서 고치고 있는 바로 그
 * 조용한 무동작을 items 쪽에 새로 만드는 셈이다.
 *
 * 모르면 보낸다. 그때 서버가 `PERMISSION_DENIED` 로 거부하는데, 그것은 이미 benign 으로
 * 분류된 기존 동작이고 백엔드가 배포되는 순간 저절로 사라진다.
 */
const isKnownReadOnly = (record) =>
  record?.permission != null && !EDIT_PERMISSIONS.includes(record.permission)

const chunk = (list, size) => {
  const out = []
  for (let i = 0; i < list.length; i += size) out.push(list.slice(i, i + size))
  return out
}

/**
 * 레코드 하나에서 교체 대상을 뽑는다.
 *
 * @returns {Promise<Array|null>} 대상 목록, 또는 **키를 못 만들어 건너뛸 레코드면 `null`**
 * @throws 복호 실패·왕복 불일치 — 무결성 신호라 호출부가 전체를 중단해야 한다 (D10)
 */
const collectFromRecord = async (record, direction) => {
  // 🛑 **F9 가드.** `resource_key` 가 없으면 `getRsKeyObjFromResource` 가
  // `createRSKeyObj()` 를 인자 없이 불러 **새 랜덤 키를 만든다**
  // (`resourceKey.js` → `pUtils.js` `createRsKey`). `group_key` 는 리소스 키를 **누가 푸느냐**만
  // 정하고 키 자체를 주지 않으므로 이 판정에 넣지 않는다 — `&& group_key == null` 로 쓰면
  // `resource_key=null + group_key 있음` 이 통과해 그 랜덤 키로 복호를 시도하게 된다.
  if (record == null || record.resource_key == null) return null

  // 🛑 **G2 — 키를 만들기 전에 할 일이 있는지 먼저 본다** (spec §7.4).
  //
  //    `rewriteV1ToV2:61` 이 "이미 v2 면 복호조차 하지 않는다"로 2회차를 무동작으로 만드는데,
  //    그 판정보다 **키 파생이 먼저** 일어나고 있었다. 키 파생은 레코드당 RSA 개인키 복호라
  //    실측 12ms — 전부 v2 가 된 뒤에도 매 언락마다 레코드 수만큼 반복됐다(dev 130건 ≈ 1.6초).
  //
  //    🛑 판정은 `isVaultV2` 로, `forwardSweep.js:61` 이 **이미 쓰는 것과 같은 함수**다.
  //    접두 검사를 여기 복제하면 매직 지식의 사본이 하나 더 생긴다.
  //    🛑 순서를 F9 가드보다 앞으로 옮기지 마라 — `resource_key` 가 없는 레코드의 `content` 를
  //    먼저 만지게 되어, 오늘은 조용히 건너뛰던 것이 전체 중단으로 바뀔 수 있다.
  const items = Array.isArray(record.items) ? record.items : []
  // 🛑 **items 는 편집 권한이 있어야 한다** — 서버가 그 계열에만 `EDIT_PERMISSIONS` 를
  //    요구한다(`vault_format_rewrite_service.py:135-136`). 없으면 매 언락마다 전량을
  //    복호·재암호화한 뒤 `PERMISSION_DENIED` 를 받는다 — 배포 전 실측에서 관측된
  //    `4× PERMISSION_DENIED` 가 그것이다.
  //    🛑 **키 파생보다 앞이다** (G2 와 같은 이유). 아래 `getRsKeyObjFromResource` 가
  //    레코드당 RSA 개인키 복호라 실측 10.58ms/회다.
  if (isKnownReadOnly(record)) return []

  const hasV1 = items.some((it) => isSweepTarget(it) && direction.hasWork(it))
  // 🛑 `null`(=skipped) 이 아니라 `[]` 다. `null` 은 "키를 못 만들어 건너뜀" 이라는 **다른 뜻**이고
  //    집계(`summary.skipped`)가 어긋난다. 대신 이 레코드는 어떤 집계에도 안 남는데, 의도한 것이다
  //    — `summary` 는 `startForwardSweep` 이 버리므로 읽히지 않는 카운터를 늘릴 이유가 없다.
  if (!hasV1) return []

  let rsKeyObj = null
  try {
    rsKeyObj = await getRsKeyObjFromResource(record)
  } catch (err) {
    // 키 파생 실패는 **접근 권한 상태**의 문제지 무결성 문제가 아니다 (D10) — 건너뛴다.
    return null
  }

  const targets = []
  // `for...of` 는 airbnb `no-restricted-syntax` 에 걸리고, `reduce` 는 순차 await 를 위해
  // 누산기를 Promise 로 감싸야 해 읽기 어려워진다. 인덱스 루프가 이 파일에서 가장 단순하다.
  for (let i = 0; i < items.length; i += 1) {
    const item = items[i]
    if (isSweepTarget(item)) {
      // eslint-disable-next-line no-await-in-loop
      const next = await direction.convert(item.content, rsKeyObj)
      // `null` = 이미 v2 이거나 카나리가 꺼져 있다 (대상 아님)
      if (next != null) {
        targets.push({ table: tableOf(item), row_id: item.id, prev: item.content, next })
      }
    }
  }
  return targets
}

/**
 * 스윕을 한 번 돌린다.
 *
 * @param {{bulkGet: Function}} resourceService `api/Resource.js`
 * @param {{rewrite: Function}} rewriteService  `api/Resource.js`
 * @param {(info: object) => void} [onError] 실패 보고 (건수·사유·행 id 만 — 암호값 금지)
 * @returns {Promise<{updated: number, skipped: number, benign: number, races: number}>}
 * @throws 복호 실패·왕복 불일치 — 전체 중단 (D10). 호출부가 잡아 Sentry 로 보낸다
 */
export const runForwardSweep = async ({
  resourceService,
  rewriteService,
  writeFormat,
  direction = FORWARD,
  // 🛑 **열쇠 2종은 `sagas.js` 가 공급한다** (PR 7-b). 이 파일은 leaf 라 `pUtils` 를 못
  //    import 하는데(0.5 실측: `env → conf → window`) 열쇠 어댑터를 만들려면 그게 필요하다.
  //    `sweepKeys` 가 false 면 열쇠는 손대지 않는다 — 리소스 카나리와 **별개 스위치**다.
  sweepKeys = false,
  userKeySource = null,
  makeGroupKeySource = null,
  onError,
}) => {
  // 🛑 **G1 — 서버가 v2 쓰기를 지시하지 않았으면 시작조차 하지 않는다** (spec §7.4 · F11).
  //
  //    초판 설계는 "카나리가 꺼지면 재암호화가 v1 을 내므로 저절로 무동작" 이라 게이트를 두지
  //    않았다. **그것은 결과에만 참이었다** — `rewriteV1ToV2`(`forwardSweep.js:61-73`)는
  //    복호·재암호화를 다 한 뒤에 결과를 보고 버린다(실측: 카나리 꺼짐 `decrypt=1 encrypt=1`).
  //    카나리가 꺼진 사용자(=현재 운영 전체)가 매 언락마다 전량을 복호·재암호화하고 있었다.
  //
  //    🛑 **값을 여기서 읽지 않고 주입받는다.** `env` 를 import 하면
  //    `env → service → conf → window` 체인이 딸려와 이 파일의 node 환경 테스트 19건이
  //    **로드 단계에서** 깨진다(실측: `ReferenceError: window is not defined`, `conf.js:249`).
  //    Task A0 이 이 모듈을 leaf 로 분리한 이유와 같은 문제다. 주입은 `sagas.js` 가 한다.
  //    🛑 **정확일치다.** 미지의 상위 포맷(예: 미래의 v3)을 만나면 스윕이 **돌지 않는다** —
  //    느슨하게(`>= 2`) 두면 "이 클라이언트가 쓸 줄 모르는 포맷"까지 대상으로 삼게 되어
  //    방향이 반대가 된다. 오늘은 도달 불가다: 주입값은 `readVaultWriteFormat`
  //    (`vaultWriteFormat.js:62-68`)의 산출물이고 그 함수가 `VAULT_WRITE_FORMAT_MAX_KNOWN`
  //    으로 상한을 씌운다. v3 라이터를 만드는 PR 이 이 줄도 함께 봐야 한다.
  if (!direction.gate(writeFormat)) {
    return { updated: 0, skipped: 0, benign: 0, races: 0 }
  }

  // 🛑 게이트 통과 후 **첫 동기 라인**. `await` 뒤에 두면 두 호출이 인터리브한다 (T18).
  if (inFlight) return { updated: 0, skipped: 0, benign: 0, races: 0 }
  inFlight = true

  const summary = { updated: 0, skipped: 0, benign: 0, races: 0 }
  try {
    // 🛑 **`user_key` 는 사용자당 1건**(`user_key.py:27` `user_id` UNIQUE)이라 페이지와 무관하다.
    //    첫 페이지 앞에 한 번만 만든다.
    const rootKeyTargets =
      sweepKeys && userKeySource != null ? await collectKeyTargets([userKeySource], direction) : []

    let offset = 0
    let hasMore = true
    while (hasMore) {
      // 4번째 인자 — Activity 를 "레코드 내보내기" 로 남기지 않는다 (`api/Resource.js` 주석 참조).
      // 정방향·역방향 스윕이 이 러너를 공유하므로 두 방향 모두 여기서 덮인다.
      // eslint-disable-next-line no-await-in-loop
      const response = await resourceService.bulkGet(SCOPE, offset, PAGE_SIZE, {
        vaultMigration: true,
      })
      const page = response?.data ?? []

      // 🛑 **페이지 지역 배열이다.** 전 페이지를 모았다가 마지막에 보내면 메모리가 사용자
      //    보유량에 비례하고(F6), 무결성 실패로 중단될 때 **앞 페이지의 검증 통과분이
      //    통째로 버려진다** (spec §7.2).
      const pageTargets = []
      const pageKeySources = []
      for (let i = 0; i < page.length; i += 1) {
        // 🛑 **G3 — 메인 스레드를 놓아준다.** 아래 `collectFromRecord` 안의 키 파생이 동기
        //    연산이라, 양보하지 않으면 페이지 전체가 하나의 long task 가 되어 첫 렌더를 막는다.
        if (i > 0 && i % YIELD_EVERY_RECORDS === 0) {
          // eslint-disable-next-line no-await-in-loop
          await yieldToEventLoop()
        }
        // eslint-disable-next-line no-await-in-loop
        const collected = await collectFromRecord(page[i], direction)
        if (collected == null) {
          summary.skipped += 1
        } else {
          pageTargets.push(...collected)
        }
        // 🛑 **읽기 전용은 사전 제외한다** (설계 D9) — `permission` 이 편집 권한(7·15)에
        //    없으면 서버가 `PERMISSION_DENIED` 로 거부한다. 요청 자체를 안 보내는 편이 낫다.
        // 🛑 **G2 와 같은 이유로 "할 일이 있나"를 키 파생보다 먼저 본다.**
        //    `makeGroupKeySource` 안이 RSA 개인키 복호다(실측 10.58ms/회, node-forge 동기).
        //    이 검사가 없으면 **전부 v2 가 된 뒤에도** 매 언락마다 그룹 공유 레코드 수만큼
        //    반복된다 — 50건이면 529ms 를 아무 일도 없이 쓴다(실측). 7-a-2 가 리소스에서
        //    없앤 비용(F11)을 열쇠 경로가 다시 만드는 형태다.
        //    🛑 판정 대상은 `group_private_key`(= `GroupKey.key`)다. `user_group_key` 가 아니다
        //       — 그건 이 값을 **여는 열쇠**이고 재암호화 대상이 아니다.
        // 🛑 **편집 권한을 보지 않는다** — 서버가 `group_key` 를 **구성원 여부**로 인가하기
        //    때문이다(`vault_format_rewrite_service.py:139-140`). 여기서 편집 권한을 요구하면
        //    읽기 전용으로만 공유된 그룹의 키가 영구 미전환으로 남는다(PR #2022 리뷰 major).
        //    `group_key` 가 응답에 실렸다는 것 자체가 이미 "그 그룹으로 접근 중" 이라는 뜻이다
        //    (`bulk_resource_service.py:24` `rk.request_model == "group"`).
        const groupBlob = page[i]?.group_key?.group_private_key
        if (
          sweepKeys &&
          makeGroupKeySource != null &&
          !!groupBlob &&
          direction.hasWork({ content: groupBlob })
        ) {
          pageKeySources.push(makeGroupKeySource(page[i]))
        }
      }

      if (pageKeySources.length > 0) {
        // eslint-disable-next-line no-await-in-loop
        pageTargets.push(...(await collectKeyTargets(pageKeySources, direction)))
      }

      if (offset === 0 && rootKeyTargets.length > 0) pageTargets.push(...rootKeyTargets)

      const chunks = chunk(pageTargets, MAX_TARGETS)
      for (let c = 0; c < chunks.length; c += 1) {
        // eslint-disable-next-line no-await-in-loop
        const result = await rewriteService.rewrite(chunks[c])
        const failed = result?.failed ?? []
        const races = failed.filter((f) => RACE_REASONS.includes(f.reason))
        const errors = failed.filter(
          (f) => !BENIGN_REASONS.includes(f.reason) && !RACE_REASONS.includes(f.reason),
        )
        summary.updated += (result?.updated ?? []).length
        summary.benign += failed.length - races.length - errors.length
        summary.races += races.length
        if (typeof onError === 'function') {
          // 🛑 암호값을 넘기지 않는다 (spec §8). 행 id 는 암호값이 아니라 포함한다 —
          //    없으면 "어느 항목이 실패했나"를 추적할 수 없다.
          //    `kind` 로 등급을 가른다 — 호출부가 Sentry 태그를 나눈다.
          if (errors.length > 0) onError({ kind: 'rewrite_failed', failed: errors })
          if (races.length > 0) onError({ kind: 'rewrite_conflict', failed: races })
        }
        // 🛑 **보고 뒤에 둔다** — 검증이 던지면 그 위의 `rewrite_failed`·`rewrite_conflict`
        //    보고가 유실된다. 열쇠 검증은 이번 요청이 실제로 바꾼 행에만 적용된다 (K8).
        // eslint-disable-next-line no-await-in-loop
        await verifySavedKeyTargets({
          targets: chunks[c],
          updatedRowIds: result?.updated ?? [],
          rollback: (t) => rewriteService.rewrite(t),
          onError,
        })
      }

      hasMore = page.length === PAGE_SIZE
      offset += PAGE_SIZE
    }
  } finally {
    inFlight = false
  }
  return summary
}

/**
 * 언락 훅이 부르는 진입점 — **절대 reject 하지 않는다.**
 *
 * `runForwardSweep` 은 무결성 실패를 던지는 것이 계약이라(D10) 순수하게 유지하고,
 * 보고·삼킴은 이 얇은 래퍼가 한다.
 *
 * 🛑 **"스윕이 실패해도 언락은 정상 종료한다"에는 한 가지 예외가 있다** (PR #1999 리뷰 minor).
 * `bulkGet`·`rewriteVaultFormat` 이 `safeboxClient` 를 타는데, 응답 body code 가
 * `509`/`403` 이면 **인터셉터가 rescue 후 `user/LOGOUT` 을 직접 dispatch 한다**
 * (`services/restClient/index.js:208` → `:183`). 그것은 이 `.catch()` **바깥**이라 막을 수 없다.
 * 즉 이 래퍼가 보장하는 것은 *"스윕의 예외가 사가로 전파되지 않는다"* 이지
 * *"스윕이 세션에 영향을 주지 않는다"* 가 아니다. 앱의 다른 모든 호출도 같은 인터셉터를
 * 타므로 새 실패 계열은 아니지만, **배경 호출이라 사용자에게는 원인이 안 보인다.** 🛑 Sentry 를 여기서 부르는 이유는 **계층 때문**이다 —
 * `redux/user/sagas.js` 가 `services/restClient` 를 import 하면 그 모듈의 `query-string`(ESM)
 * 체인이 딸려와 기존 사가 테스트가 파싱 단계에서 깨진다(실측). `services/sentryLog` 는
 * `@sentry/react` 하나만 import 하는 leaf 다.
 */
/**
 * 실패 종류별 Sentry 태그.
 *
 * - `CONFLICT` 는 **정상 동시성**이라 분리한다 — 같은 area 로 보내면 실제 오류와 섞여
 *   둘 다 무시하게 된다 (PR #1999 리뷰 minor).
 * - `key_verify_failed` 는 **등급이 다르다** — 저장한 열쇠가 다시 안 열린 것이고, `user_key`
 *   면 그 사용자가 로그인을 못 하게 되는 계열이다(spec G1). 일상적 교체 실패와 같은 태그로
 *   보내면 알림 규칙이 그 둘을 가를 수 없다.
 */
const sentryArea = (area, kind) => {
  if (kind === 'rewrite_conflict') return `${area}.conflict`
  if (kind === 'key_verify_failed') return `${area}.key_verify`
  return area
}

const startSweep = (area, deps) =>
  runForwardSweep({
    ...deps,
    // `CONFLICT` 는 **정상 동시성**이라 태그를 분리한다 — 같은 area 로 보내면 Sentry 에서
    // 실제 오류와 섞여 둘 다 무시하게 된다 (PR #1999 리뷰 minor).
    onError: (info) =>
      captureHandledError(sentryArea(area, info.kind), new Error(`${area}_${info.kind}`), info),
  }).catch((err) => captureHandledError(area, err))

export const startForwardSweep = (deps) => startSweep('vault_forward_sweep', deps)

/**
 * **역방향 진입점** — v2 를 v1 으로 되돌린다 (PR 7-b, 설계 spec §2.2 K6).
 *
 * 🛑 **UI 로 노출하지 않는다.** 사고 대응은 드물고 신중해야 하는 작업이라 버튼은 오클릭
 * 위험이 있다. 개발자 콘솔에서 명시적으로 부른다.
 *
 * 🛑 **수동 실행 전에 `VAULT_V2_SWEEP_KEYS_WORKSPACE_IDS` 를 먼저 비운다** (spec §5.3).
 * 플래그가 켜진 채로 되돌리면 **다음 언락에 정방향이 다시 v2 로 만든다** — 되돌리기가
 * 무효가 된다. 자동 경로(K8 재조회 실패)는 같은 세션에서 중단하므로 이 문제가 없다.
 *
 * 🛑 **태그를 서술자가 아니라 여기서 정한다** (1회차 리뷰 C3). 태그는 "무엇을 하는가"가
 * 아니라 **"누가 불렀는가"** 의 속성이다 — 역방향 사고 대응 중에 올라온 이벤트가 정방향
 * 태그로 찍히면 운영자가 가장 혼란스러운 순간에 잘못된 라벨을 본다.
 */
export const startReverseSweep = (deps) => startSweep('vault_reverse_sweep', deps)

export default runForwardSweep
